import Vue from "vue";
import i18n from "@/plugins/i18n";
import router from "@/router";

export default function ({ globals }) {
	return {
		namespaced: true,
		state: {
			code: null,
			recoveryCode: null,
			identifier: null,
			MfaChallengeName: null,
			password: null,
			isSupportUser: null,
			loginAsUserIdentifier: null,
			session: {},
			inlineErrors: {
				recoveryKeyNotValid: null,
			},
		},
		mutations: {
			SET_IDENTIFIER(state, value) {
				state.identifier = value ? value.replace(/\s/g, "") : value;
			},
			SET_PASSWORD(state, value) {
				state.password = value;
			},
			SET_IS_SUPPORT_USER(state, value) {
				state.isSupportUser = value;
			},
			SET_INLINE_ERROR(state, { key, value }) {
				Vue.set(state.inlineErrors, key, value);
			},
			SET_LOG_IN_AS_IDENTIFIER(state, value) {
				state.loginAsUserIdentifier = value ? value.replace(/\s/g, "") : value;
			},
			SET_CODE(state, value) {
				state.code = value ? value.replace(/\s/g, "") : value;
			},
			SET_RECOVERY_CODE(state, value) {
				state.recoveryCode = value ? value.replace(/\s/g, "") : value;
			},
			SET_SESSION(state, value) {
				state.session = value;
			},
			SET_MFA_CHALLENGE_NAME(state, value) {
				state.MfaChallengeName = value;
			},
		},
		actions: {
			setIdentifier({ commit }, identifier) {
				commit("SET_IDENTIFIER", identifier);
			},
			setMFAChallenge({ commit }, { challengeName, session }) {
				commit("SET_MFA_CHALLENGE_NAME", challengeName);
				commit("SET_SESSION", session);
			},
			async signIn({ state, commit, dispatch }) {
				try {
					const {
						identifier,
						password,
						isSupportUser,
						loginAsUserIdentifier,
					} = state;

					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const response = await fetch(`${globalConfig.backends.users}/signin`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							identifier,
							password,
							isSupportUser,
							loginAsUserIdentifier,
						}),
					});

					const { result, error = null } = await response.json();

					if (error) throw new Error(error.code);

					if (result.challengeName) {
						commit("SET_SESSION", result.session);
						commit("SET_MFA_CHALLENGE_NAME", result.challengeName);
						return;
					}

					dispatch("setChamaileonCookies", result.tokens, { root: true });

					if (state.loginAsUserIdentifier) {
						this._vm.$chamaileon.pushToGtmDataLayer({
							event: "loginAs",
							userId: result.userId,
							adminId: result.adminId,
						});
					} else {
						this._vm.$chamaileon.pushToGtmDataLayer({
							event: "login",
							userId: result.userId,
						});
					}

					router.push({ path: "/" }).catch(() => {});
				} catch (error) {
					const translationExists = i18n.te(`loginComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`loginComponent.errorMessages.${code}`),
						target: {
							uid: "logoutErrors",
							type: "flagToast",
							timeout: 4000,
						},
					}, { root: true });

					this._vm.$chamaileon.pushToGtmDataLayer({
						event: "unsuccessfulLogin",
					});
					return;
				}
			},
			async signInConfirmMfa({ state, commit, dispatch }) {
				try {
					const {
						code,
						session,
						identifier,
						isSupportUser,
						loginAsUserIdentifier,
					} = state;

					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const response = await fetch(`${globalConfig.backends.users}/mfa/confirm`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							code,
							session,
							identifier,
							isSupportUser,
							loginAsUserIdentifier,
						}),
					});

					const { result, error = null } = await response.json();

					if (error) throw new Error(error.code);

					dispatch("setChamaileonCookies", result.tokens, { root: true });

					router.push({ path: "/" }).catch(() => {});
				} catch (error) {
					const translationExists = i18n.te(`MfaComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";
					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`MfaComponent.errorMessages.${code}`),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });

					if (code === "codeMismatchException") {
						commit("SET_CODE", "");
					}

					return;
				}
			},
			async recoverMFAAccount({ state, commit, dispatch }) {
				try {
					const {
						identifier,
						recoveryCode,
					} = state;

					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const response = await fetch(`${globalConfig.backends.users}/recover-mfa-user`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							identifier,
							recoveryCode,
						}),
					});

					const { error = null } = await response.json();

					if (error) throw new Error(error.code);

					dispatch("logger/logSuccess", {
						id: Date.now(),
						handled: true,
						source: "backend",
						notification: i18n.t("MfaRecoveryComponent.successMessage"),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });
					// we let the user log in with credentials already inserted
					await dispatch("signIn");
					commit("SET_MFA_CHALLENGE_NAME", null);
				} catch (error) {
					const translationExists = i18n.te(`loginComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					commit("SET_INLINE_ERROR", {
						key: "recoveryKeyNotValid",
						value: i18n.t(`loginComponent.errorMessages.${code}`),
					});

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`loginComponent.errorMessages.${code}`),
					}, { root: true });
				}
			},
		},
	};
}
